import { Button, FormControl, FormControlLabel, MenuItem, Skeleton, Switch, TextField } from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";
import AiChatPreview from "./ChatPreview";
import CaseAutocomplete from "components/CaseAutocomplete";
import { useAppSelector } from "redux/hooks";
import { fetchAiChats, queryAiChats, selectLensEditorAiChats, selectLensEditorForm } from "redux/slices/lensEditor";
import { Outlet, useNavigate, useParams } from "react-router";

import DebouncedText from "components/DebouncedText";
import useAppDispatchWithSnackbar from "helpers/hooks/useAppDispatchWithSnackbar";
import { fetchCaseIntakes } from "redux/slices/case";
import {
  CellValuePredicateInput,
  DocumentIntake,
  DocumentSegment,
  FormField,
  SearchCasesResult,
} from "generated/graphql";
import StyledAutocomplete from "components/StyledAutocomplete";
import { CellValuePredicateBooleanSet } from "components/CellValuePredicateList";
import PSelect from "components/PSelect";

const AiChatBrowser: FC = () => {
  const snackpatch = useAppDispatchWithSnackbar();
  const lensIdParams = useParams<{ lensId: string }>().lensId;
  const lensId = parseInt(lensIdParams || "");
  const aiChats = useAppSelector(selectLensEditorAiChats);
  const [shuffleOrder, setShuffleOrder] = useState<boolean>(false);
  const [chatsMode, setChatsMode] = useState<"search" | "history">("history");
  const [lookbackHours, setLookbackHours] = useState<number>(1);
  const [statusQuery, setStatusQuery] = useState<string | undefined>();
  const params = useParams();
  const selectedChatId = params.chatId;
  const navigate = useNavigate();
  const [caseId, setCaseId] = useState<number | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const form = useAppSelector(selectLensEditorForm);
  const [fieldId, setFieldId] = useState<number>(form.fields[0]?.id ?? -1);
  const [documentId, setDocumentId] = useState<number | undefined>(undefined);
  const [pageNumber, setPageNumber] = useState<number | undefined>(undefined);
  const [documentIntakes, setDocumentIntakes] = useState<DocumentIntake[]>([]);
  const documentSegments = useMemo(() => documentIntakes.flatMap((d) => d.documentSegments), [documentIntakes]);
  const [cellValuePredicateList, setCellValuePredicateList] = useState<CellValuePredicateInput[][]>([]);

  const searchMode = chatsMode === "search";

  const selectedField: FormField | undefined = form.fields.find((f) => f.id === fieldId);

  useEffect(() => {
    if (searchMode) return;
    setIsLoading(true);
    const fetch = fetchAiChats({
      lensId,
      caseIds: caseId ? [caseId] : undefined,
      fieldId,
      documentId,
      pageNumber,
      status: statusQuery,
      offset: 0,
      limit: 100,
    });
    void snackpatch(fetch, {
      errorMessage: "Failed to fetch AI chats",
    }).then(() => setIsLoading(false));
  }, [lensId, caseId, fieldId, documentId, pageNumber, statusQuery, searchMode]);

  // when ai chats updates from the dispatch, scroll to the selected chat
  useEffect(() => {
    if (!selectedChatId) {
      return;
    }
    const chatElement = document.getElementById(selectedChatId);
    if (chatElement) {
      chatElement.scrollIntoView();
    }
  }, [aiChats]);

  const onCaseChangeHandler = (selectedCase: SearchCasesResult | undefined) => {
    setCaseId(selectedCase?.caseId);
    if (!selectedCase) {
      setDocumentId(undefined);
      setPageNumber(undefined);
      setDocumentIntakes([]);
    } else {
      void snackpatch(
        fetchCaseIntakes({
          caseId: selectedCase.caseId,
          forceRefresh: false,
        }),
        {
          errorMessage: "Failed to fetch Case Intakes",
        }
      ).then((intakes: DocumentIntake[]) => setDocumentIntakes(intakes));
    }
  };

  const handleSearch = () => {
    setIsLoading(true);
    void snackpatch(
      queryAiChats({
        lensId,
        fieldId,
        minCreatedAt: new Date(Date.now() - lookbackHours * 60 * 60 * 1000).toISOString(),
        cellCriteria: cellValuePredicateList,
        shuffleOrder,
      }),
      {
        errorMessage: "Failed to search chats by response",
      }
    ).finally(() => setIsLoading(false));
  };

  return (
    <div className="flex flex-col p-2">
      <div className="flex flex-col">
        <div className="flex flex-row items-center">
          <StyledAutocomplete<FormField, false, true, false>
            freeSolo={false}
            multiple={false}
            disableClearable={true}
            options={form.fields}
            renderInput={(params) => (
              <TextField {...params} color="secondary" label="Field" variant="outlined" sx={{ width: "250px" }} />
            )}
            renderOption={(htmlAttrs, f) => (
              <li {...htmlAttrs} key={f.id}>
                {f.name}
              </li>
            )}
            value={form.fields.find((f) => f.id === fieldId)}
            onChange={(_evt, item) => {
              setFieldId(item.id);
            }}
            getOptionLabel={(f: FormField) => f.name}
          />
          <PSelect
            label="Status"
            value={statusQuery}
            disabled={searchMode}
            onChange={(evt) => setStatusQuery((evt.target.value as string) || undefined)}>
            <MenuItem value=""></MenuItem>
            <MenuItem value="complete">complete</MenuItem>
            <MenuItem value="failed_final_validation">failed_final_validation</MenuItem>
            <MenuItem value="rate_limit_exceeded">rate_limit_exceeded</MenuItem>
            <MenuItem value="partial_failed_final_validation">partial_failed_final_validation</MenuItem>
            <MenuItem value="unexpected">unexpected</MenuItem>
            <MenuItem value="400_400">400_400</MenuItem>
            <MenuItem value="parse_failure">parse_failure</MenuItem>
            <MenuItem value="openai_timeout">openai_timeout</MenuItem>
            <MenuItem value="openai_internal_server_error">openai_internal_server_error</MenuItem>
          </PSelect>
          <div className="w-[200px]">
            <CaseAutocomplete lensId={lensId} label="Case" onChange={onCaseChangeHandler} disabled={searchMode} />
          </div>
          {caseId !== undefined && (
            <>
              <FormControl>
                <StyledAutocomplete<DocumentSegment, false, false, false>
                  freeSolo={false}
                  multiple={false}
                  disabled={false}
                  options={documentSegments}
                  renderInput={(params) => (
                    <TextField {...params} color="secondary" label="Document Id" variant="outlined" fullWidth />
                  )}
                  renderOption={(htmlAttrs, d) => (
                    <li {...htmlAttrs} key={d.id}>
                      {d.id}
                    </li>
                  )}
                  onChange={(_evt, doc) => {
                    setDocumentId(doc?.id);
                    if (!doc) {
                      setPageNumber(undefined);
                    }
                  }}
                  getOptionLabel={(d: DocumentSegment) => d.id.toString()}
                />
              </FormControl>
              <DebouncedText
                label="Page Number"
                value={pageNumber ? pageNumber.toString() : ""}
                onChange={(v) => setPageNumber(parseInt(v))}
              />
            </>
          )}
          <div className="flex flex-row items-center">
            <Switch checked={searchMode} onChange={(_evt, value) => setChatsMode(value ? "search" : "history")} />
            <p>Response Search</p>
          </div>
        </div>
        {searchMode && selectedField && (
          <div className="flex flex-row items-center h-[75px] gap-1">
            <Button onClick={handleSearch} disabled={isLoading}>
              Go
            </Button>
            <TextField
              sx={{ maxWidth: "100px" }}
              type="number"
              label="Lookback Hours"
              value={lookbackHours.toString()}
              onChange={(evt) => setLookbackHours(Number.parseInt(evt.target.value) ?? 0)}
            />
            <p className="text-sm mr-1">Matching chats: {aiChats.length}</p>
            <FormControlLabel
              control={<Switch checked={shuffleOrder} onChange={(_evt, value) => setShuffleOrder(value)} />}
              label="Shuffle Order"
            />
            <CellValuePredicateBooleanSet
              key={selectedField.id}
              field={selectedField}
              values={cellValuePredicateList}
              setValues={setCellValuePredicateList}
            />
          </div>
        )}
        {!searchMode && <div className="min-h-[75px]"></div>}
      </div>
      <div className="flex flex-row flex-1 overflow-y-auto">
        <div className="flex flex-col gap-2 overflow-y-auto h-[80vh] w-[275px]">
          {isLoading ? (
            <>
              <Skeleton animation="wave" variant="rectangular" width="100%" height={100} />
              <Skeleton animation="wave" variant="rectangular" width="100%" height={100} />
              <Skeleton animation="wave" variant="rectangular" width="100%" height={100} />
            </>
          ) : (
            aiChats.map((chat) => (
              <AiChatPreview
                key={chat.id}
                chat={chat}
                selected={selectedChatId === chat.id}
                onClick={() => navigate(chat.id)}
              />
            ))
          )}
        </div>
        <div className="flex-1 h-[80vh]">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default AiChatBrowser;
